import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Mail, 
  Phone, 
  Calendar,
  MessageSquare,
  ChevronDown,
  Trash2,
  Eye,
  X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const statusColors = {
  new: 'bg-amber-100 text-amber-700',
  contacted: 'bg-blue-100 text-blue-700',
  booked: 'bg-green-100 text-green-700'
};

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchLeads = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/leads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const updateStatus = async (leadId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/leads/${leadId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        setLeads(leads.map(lead => 
          lead.id === leadId ? { ...lead, status: newStatus } : lead
        ));
        toast.success(`Lead status updated to "${newStatus}"`);
      } else {
        toast.error('Failed to update lead status');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update lead status');
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/leads/${leadId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setLeads(leads.filter(lead => lead.id !== leadId));
        setSelectedLead(null);
        toast.success('Lead deleted successfully');
      } else {
        toast.error('Failed to delete lead');
      }
    } catch (error) {
      console.error('Failed to delete lead:', error);
      toast.error('Failed to delete lead');
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         lead.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || lead.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-sage-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="admin-leads">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-light text-charcoal mb-2">Leads Pipeline</h1>
        <p className="text-charcoal/60">Manage contact form submissions</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal/40" />
          <Input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-11 bg-white border-border/50 rounded-xl"
            data-testid="leads-search"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-40 h-11 bg-white border-border/50 rounded-xl" data-testid="leads-filter">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="booked">Booked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Leads List */}
      {filteredLeads.length === 0 ? (
        <Card className="bg-white border-0 shadow-soft rounded-xl">
          <CardContent className="p-12 text-center">
            <MessageSquare className="w-12 h-12 text-charcoal/20 mx-auto mb-4" />
            <p className="text-charcoal/60">No leads found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredLeads.map((lead, index) => (
            <motion.div
              key={lead.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="bg-white border-0 shadow-soft hover:shadow-medium transition-all rounded-xl overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Lead Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-charcoal">{lead.name}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${statusColors[lead.status]}`}>
                          {lead.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-charcoal/60">
                        <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-sage-600 transition-colors" data-testid="lead-email-link">
                          <Mail className="w-4 h-4" />
                          {lead.email}
                        </a>
                        {lead.phone && (
                          <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-sage-600 transition-colors" data-testid="lead-phone-link">
                            <Phone className="w-4 h-4" />
                            {lead.phone}
                          </a>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(lead.created_at)}
                        </span>
                      </div>
                      {lead.service_interest && (
                        <p className="text-sm text-sage-600 mt-2">
                          Interested in: {lead.service_interest}
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Select
                        value={lead.status}
                        onValueChange={(value) => updateStatus(lead.id, value)}
                      >
                        <SelectTrigger className="w-32 h-9 text-sm bg-cream-50 border-border/50 rounded-lg" data-testid={`status-select-${lead.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="booked">Booked</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedLead(lead)}
                        className="text-charcoal/60 hover:text-charcoal"
                        data-testid={`view-lead-${lead.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteLead(lead.id)}
                        className="text-charcoal/60 hover:text-red-600"
                        data-testid={`delete-lead-${lead.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-charcoal/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-lg max-w-lg w-full max-h-[80vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-border/50 flex items-center justify-between">
              <h2 className="font-serif text-2xl text-charcoal">Lead Details</h2>
              <button
                onClick={() => setSelectedLead(null)}
                className="p-2 hover:bg-cream-50 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm text-charcoal/60">Name</label>
                <p className="font-medium text-charcoal">{selectedLead.name}</p>
              </div>
              <div>
                <label className="text-sm text-charcoal/60">Email</label>
                <p className="font-medium text-charcoal">{selectedLead.email}</p>
              </div>
              {selectedLead.phone && (
                <div>
                  <label className="text-sm text-charcoal/60">Phone</label>
                  <p className="font-medium text-charcoal">{selectedLead.phone}</p>
                </div>
              )}
              {selectedLead.service_interest && (
                <div>
                  <label className="text-sm text-charcoal/60">Service Interest</label>
                  <p className="font-medium text-charcoal">{selectedLead.service_interest}</p>
                </div>
              )}
              <div>
                <label className="text-sm text-charcoal/60">Message</label>
                <p className="text-charcoal bg-cream-50 p-4 rounded-xl mt-1">
                  {selectedLead.message}
                </p>
              </div>
              <div>
                <label className="text-sm text-charcoal/60">Submitted</label>
                <p className="font-medium text-charcoal">{formatDate(selectedLead.created_at)}</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminLeads;
